import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Loader2, Save, Trash2, ExternalLink, ChevronDown, ChevronUp, AlertCircle, X } from "lucide-react";
import { toast } from "sonner";

export default function AdminClaims() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [meta, setMeta] = useState(null);
  const [f, setF] = useState({ status: "", claim_type: "", waiting_on: "" });
  const [expanded, setExpanded] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState({});

  const load = async () => {
    const params = Object.entries(f).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
    const [r, m] = await Promise.all([
      api.get(`/admin/claims${params ? "?" + params : ""}`),
      meta ? Promise.resolve({ data: meta }) : api.get("/admin/claims/meta"),
    ]);
    setRows(r.data);
    if (!meta) setMeta(m.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [f]);

  const startEdit = (c) => {
    setExpanded(c.claim_id);
    setDraft({
      current_status: c.current_status,
      cp_exam_date: c.cp_exam_date || "",
      status_note: c.status_note || "",
      waiting_on: c.waiting_on || "",
      denial_action: c.denial_action || "",
    });
  };
  const cancelEdit = () => { setExpanded(null); setDraft({}); };

  const save = async (cid) => {
    setSaving({ ...saving, [cid]: true });
    try {
      // Send the full editable set — backend treats "" on CLEARABLE fields as an explicit clear.
      await api.patch(`/claims/${cid}`, draft);
      toast.success("Saved");
      cancelEdit();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving({ ...saving, [cid]: false }); }
  };

  const remove = async (cid) => {
    if (!window.confirm("Delete this claim? The veteran will no longer see it.")) return;
    await api.delete(`/claims/${cid}`);
    load();
  };

  if (!user || user.role !== "admin") return <div className="p-24 text-center">Admin only.</div>;
  if (!rows || !meta) return <div className="grid place-items-center h-96"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="mx-auto max-w-[1600px] px-5 lg:px-10 py-10">
      <div className="mb-8">
        <Link to="/admin" className="text-sm text-primary/60 hover:text-primary font-semibold" data-testid="back-to-admin">← Back to Admin</Link>
        <div className="mt-4 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="label-caps text-primary/70">Admin, Claims</div>
            <h1 className="font-display text-3xl lg:text-5xl mt-1">Claim status tracker</h1>
            <p className="text-primary/70 mt-2 max-w-2xl">All veteran claims across the pipeline. Filter, then click a row to update status, note, and Waiting On in two clicks.</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-5 border-2 mb-6" data-testid="claims-filters">
        <div className="grid md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-primary/70">Status</label>
            <Select value={f.status || "__all__"} onValueChange={(v) => setF({ ...f, status: v === "__all__" ? "" : v })}>
              <SelectTrigger className="mt-1 h-11" data-testid="filter-status"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent><SelectItem value="__all__">Any</SelectItem>{meta.stages.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-primary/70">Claim Type</label>
            <Select value={f.claim_type || "__all__"} onValueChange={(v) => setF({ ...f, claim_type: v === "__all__" ? "" : v })}>
              <SelectTrigger className="mt-1 h-11" data-testid="filter-type"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent><SelectItem value="__all__">Any</SelectItem>{meta.claim_types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-primary/70">Waiting On</label>
            <Select value={f.waiting_on || "__all__"} onValueChange={(v) => setF({ ...f, waiting_on: v === "__all__" ? "" : v })}>
              <SelectTrigger className="mt-1 h-11" data-testid="filter-waiting"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent><SelectItem value="__all__">Any</SelectItem>{meta.waiting_on.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <button onClick={() => setF({ status: "", claim_type: "", waiting_on: "" })} className="h-11 text-sm text-primary hover:underline flex items-center gap-1" data-testid="filter-clear">
              <X className="w-3.5 h-3.5" /> Clear
            </button>
          </div>
        </div>
        <div className="mt-4 text-sm text-primary/70">{rows.length} claim{rows.length === 1 ? "" : "s"}</div>
      </Card>

      {/* Table */}
      <Card className="p-0 border-2 overflow-x-auto">
        <table className="min-w-[1100px] w-full text-sm" data-testid="claims-table">
          <thead className="bg-secondary">
            <tr>
              <th className="text-left p-3 font-semibold w-4"></th>
              <th className="text-left p-3 font-semibold">Veteran</th>
              <th className="text-left p-3 font-semibold">Type</th>
              <th className="text-left p-3 font-semibold">Current status</th>
              <th className="text-left p-3 font-semibold">Waiting on</th>
              <th className="text-left p-3 font-semibold">Updated</th>
              <th className="text-center p-3 font-semibold">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <React.Fragment key={c.claim_id}>
                <tr className="border-t border-border hover:bg-secondary/40" data-testid={`row-${c.claim_id}`}>
                  <td className="p-3">
                    <button onClick={() => expanded === c.claim_id ? cancelEdit() : startEdit(c)} data-testid={`toggle-${c.claim_id}`}>
                      {expanded === c.claim_id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  </td>
                  <td className="p-3">
                    <Link to={`/admin/veteran/${c.veteran_id}`} className="font-semibold hover:text-accent-foreground inline-flex items-center gap-1">
                      {c.veteran_name} <ExternalLink className="w-3 h-3 opacity-40" />
                    </Link>
                    <div className="text-xs text-primary/50">{c.veteran_email}</div>
                  </td>
                  <td className="p-3"><span className="px-2 py-1 text-xs bg-secondary border">{c.claim_type}</span></td>
                  <td className="p-3">{c.current_status}</td>
                  <td className="p-3">
                    {c.waiting_on ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary">
                        <AlertCircle className="w-3 h-3" /> {c.waiting_on}
                      </span>
                    ) : <span className="text-xs text-primary/40">—</span>}
                  </td>
                  <td className="p-3 text-xs text-primary/60">{new Date(c.updated_at).toLocaleDateString()}</td>
                  <td className="p-3 text-center">
                    <Button size="sm" variant={expanded === c.claim_id ? "default" : "outline"} onClick={() => expanded === c.claim_id ? cancelEdit() : startEdit(c)} data-testid={`edit-${c.claim_id}`}>
                      {expanded === c.claim_id ? "Close" : "Update"}
                    </Button>
                  </td>
                </tr>
                {expanded === c.claim_id && (
                  <tr className="bg-secondary/50">
                    <td colSpan={7} className="p-6">
                      <div className="grid md:grid-cols-4 gap-4" data-testid={`editor-${c.claim_id}`}>
                        <div className="md:col-span-2">
                          <label className="text-xs font-semibold uppercase tracking-widest text-primary/70">Move to status</label>
                          <Select value={draft.current_status} onValueChange={(v) => setDraft({ ...draft, current_status: v })}>
                            <SelectTrigger className="mt-1 h-11 bg-white" data-testid="edit-status"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {meta.stages
                                .filter((s) => !(c.claim_type === "Higher-Level Review" && meta.hlr_skip.includes(s)))
                                .map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-xs font-semibold uppercase tracking-widest text-primary/70">Waiting on</label>
                          <Select value={draft.waiting_on || "__none__"} onValueChange={(v) => setDraft({ ...draft, waiting_on: v === "__none__" ? "" : v })}>
                            <SelectTrigger className="mt-1 h-11 bg-white" data-testid="edit-waiting"><SelectValue placeholder="No blocker" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">No blocker</SelectItem>
                              {meta.waiting_on.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-xs font-semibold uppercase tracking-widest text-primary/70">C&P Exam date</label>
                          <Input type="date" value={draft.cp_exam_date} onChange={(e) => setDraft({ ...draft, cp_exam_date: e.target.value })} className="mt-1 h-11 bg-white" data-testid="edit-cp" />
                        </div>
                        <div className="md:col-span-4">
                          <label className="text-xs font-semibold uppercase tracking-widest text-primary/70">Status note (visible to veteran)</label>
                          <Textarea rows={2} value={draft.status_note} onChange={(e) => setDraft({ ...draft, status_note: e.target.value })} className="mt-1 bg-white" placeholder="Waiting for the nexus letter from Dr. X" data-testid="edit-note" />
                        </div>
                        {draft.current_status === "Denied" && (
                          <div className="md:col-span-2">
                            <label className="text-xs font-semibold uppercase tracking-widest text-primary/70">Follow-up action (log-only)</label>
                            <Select value={draft.denial_action || "__none__"} onValueChange={(v) => setDraft({ ...draft, denial_action: v === "__none__" ? "" : v })}>
                              <SelectTrigger className="mt-1 h-11 bg-white" data-testid="edit-denial"><SelectValue placeholder="None yet" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">None yet</SelectItem>
                                {meta.denial_actions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-primary/60 mt-1">To start a new tracker, open the veteran's detail page and create a new Supplemental or Higher-Level Review claim.</p>
                          </div>
                        )}
                        <div className="md:col-span-4 flex items-center justify-between pt-3 border-t border-border">
                          <button onClick={() => remove(c.claim_id)} className="text-xs text-destructive hover:underline flex items-center gap-1" data-testid="claim-delete">
                            <Trash2 className="w-3.5 h-3.5" /> Delete claim
                          </button>
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={cancelEdit}>Cancel</Button>
                            <Button size="sm" onClick={() => save(c.claim_id)} disabled={saving[c.claim_id]} data-testid="claim-save">
                              <Save className="w-3.5 h-3.5" /> {saving[c.claim_id] ? "Saving…" : "Save"}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-primary/50">No claims match. Create a claim from a veteran's detail page.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
